import type { FunctionComponent, ReactElement, ReactNode } from 'react';
import { Fragment } from 'react';

import type { ISingleHistoryBet, ISingleHistoryGame, ISingleHistorySeat } from '../../../../api/fetchers/history/types';
import type { TableConfigType } from '../../../../api/fetchers/tableConfig/types';
import { useCurrencyFormatterConnected } from '../../../../hooks/useCurrencyFormatterConnected';
import { useCurrencySymbol } from '../../../../hooks/useCurrencySymbol';
import { useConfigSlice } from '../../../../store/slices/config/selectors';
import { useSettingsSlice } from '../../../../store/slices/settings/selectors';
import { getIsLanguageRTL } from '../../../../utils/language';
import { CurrencyConnected } from '../../../currency/CurrencyConnected';
import { MaxPayoutText } from '../../../maxPayoutText/MaxPayoutText';
import { L12nText } from '../../../text/L12nText';
import { useHistoryNavigation } from '../../hooks/useHistoryNavigation';

import { NetCash } from './components/NetCash';
import { ResultMessage } from './components/ResultsMessage';
import { AndarbaharTableResult } from './results/andarbahar/AndarbaharTableResult';
import BingoTableResult from './results/bingo/BingoTableResult';
import BlackjackTableResult from './results/blackjack/BlackjackTableResult';
import BoomorbustTableResult from './results/boomorbust/BoomorbustTableResult';
import MegaSicBacTableResult from './results/megasicbac/MegaSicBacTableResult';
import BlackjackOneTableResult from './results/one_blackjack/BlackjackOneTableResult';
import SweetBonanzaTableResult from './results/sweetbonanza/SweetBonanzaTableResult';
import TreasureIslandTableResult from './results/treasureIsland/TreasureIslandTableResult';

import styles from './HistoryBetInfo.module.css';

export interface HistoryBetInfoProps {
  type: string;
  bets: ISingleHistoryBet[];
  game: ISingleHistoryGame;
  seats: ISingleHistorySeat[];
  config: TableConfigType | null;
  inNavDrawer: boolean;
}

export const HistoryBetInfo: FunctionComponent<HistoryBetInfoProps> = ({
  type,
  bets,
  game,
  seats,
  inNavDrawer,
}: HistoryBetInfoProps) => {
  const { isLoadingDetails } = useHistoryNavigation();
  const gameType = type.toUpperCase();
  const isMaxPayout = bets.some((bet) => bet.maxcap === 'true');
  const config = useConfigSlice.tableConfig();
  const currencyFormatter = useCurrencyFormatterConnected();
  const currencySymbol = useCurrencySymbol();
  const locale = useSettingsSlice.currentLanguage();
  const isLanguageRTL = getIsLanguageRTL(locale);

  const renderTable = (): ReactElement => {
    switch (gameType) {
      case 'ONEBJ':
        return <BlackjackOneTableResult seats={seats as never} tableClass={styles.table} />;
      case 'BINGO':
        return <BingoTableResult bets={bets} game={game} />;
      case 'TREASUREADVENTURE':
        return <TreasureIslandTableResult bets={bets} game={game} />;
      case 'BOOMORBUST':
        return <BoomorbustTableResult bets={bets} game={game} />;
      case 'MEGASICBAC':
        return <MegaSicBacTableResult bets={bets} game={game} seats={seats} />;
    }

    return (
      <>
        <thead>
          <tr>
            {/* <td className={styles.table_head}></td> */}
            <td className={styles.table_head}>
              <L12nText tk="BETTYPE" />
            </td>
            {bets[0]?.playerMul ? (
              <td className={styles.table_head}>
                <L12nText tk="MULTIPLIER_FULL" />
              </td>
            ) : null}
            <td className={styles.table_head} dir={isLanguageRTL ? 'rtl' : 'ltr'}>
              <L12nText tk="BET_LABEL" /> {currencySymbol}
            </td>
            <td className={styles.table_head} dir={isLanguageRTL ? 'rtl' : 'ltr'}>
              <L12nText tk="NETCASH" /> {currencySymbol}
            </td>
          </tr>
        </thead>
        <tbody>
          {bets.map((bet: ISingleHistoryBet, index: number) => {
            return (
              <Fragment key={index}>
                <tr>
                  <td>{getDescription(bet.description)}</td>
                  {bet.playerMul ? <td>{bet.playerMul}</td> : null}
                  <td>
                    <CurrencyConnected hideCurrency value={Number(bet.amount)} />
                  </td>
                  <td className={Number(bet.netCash) > 0 ? styles.win : styles.lose}>
                    {game.cancelled && Number(bet.netCash) == 0 ? (
                      <span className={styles.refunded}>
                        <L12nText tk="REFUNDED" />
                      </span>
                    ) : (
                      <NetCash bet={bet} />
                    )}
                  </td>
                </tr>
              </Fragment>
            );
          })}
        </tbody>
      </>
    );
  };

  if (isLoadingDetails) return null;
  return (
    <div className={`${styles.HistoryBetInfoContainer} ${inNavDrawer ? styles.HistoryBetInfoContainer_drawer : ''}`}>
      <ResultMessage game={game} type={gameType} />
      <div className={styles.HistoryBetInfo}>
        <div>
          {((type: string, seats: ISingleHistorySeat[]): ReactNode => {
            switch (type) {
              case 'ANDARBAHAR':
                return <AndarbaharTableResult seats={seats as never} />;
            }
          })(gameType, seats)}

          <table className={styles.table}>{renderTable()}</table>

          {((type: string, game: ISingleHistoryGame): ReactNode => {
            switch (type) {
              case 'BLACKJACK':
                return <BlackjackTableResult seats={seats as never} tableClass={styles.table} />;
              case 'SWEETBONANZA':
                return <SweetBonanzaTableResult additional={game.additional.sweetbonanza} />;
            }
          })(gameType, game)}
        </div>
        {((type: string, game: ISingleHistoryGame): ReactNode => {
          switch (type) {
            case 'MEGASICBAC':
              return (
                <MaxPayoutText
                  tk="MAXIMUM_PAYOUT_V4"
                  dynamicValues={[currencyFormatter(config?.payoutBetMaxLimit || 0) || '']}
                  showAsterisk
                  isHidden={!isMaxPayout}
                />
              );
            case 'HIGHFLYER':
              return (
                <MaxPayoutText
                  tk="MAXIMUM_PAYOUT_V5"
                  dynamicValues={[`${config?.maxMultiplier||0}`, currencyFormatter(config?.payoutBetMaxLimit || 0) || '']}
                  showAsterisk
                  isHidden={!isMaxPayout}
                  />
              );
            default:
              return <MaxPayoutText showAsterisk isHidden={!isMaxPayout} />;
          }
        })(gameType, game)}
      </div>
    </div>
  );
};

const getDescription = (text: string): string => {
  if (text[0] == '{') {
    const parsed = JSON.parse(text);
    return parsed.cardNo || parsed[Object.keys(parsed)[0]];
  }
  return text;
};